/**
 * SLAM telemetry — the instrument panel.
 *
 * SLAM fails in ways you cannot reproduce by asking ("it drifted near the
 * window"). This records, per tracked frame: state, feature count, pose,
 * per-stage latency, and drift from origin; keeps a ring buffer for the HUD and
 * for export alongside a recorded dataset; and aggregates the numbers we hold
 * ourselves to as ship gates.
 */

export type TrackingState =
  | 'idle'
  | 'initializing'
  | 'tracking'
  | 'lost'
  | 'reset'
  | 'unavailable';

export interface TelemetrySample {
  /** ms since telemetry start. */
  t: number;
  frameId: number;
  state: TrackingState;
  featureCount: number;
  /** camera position in world units, if tracked. */
  pos: [number, number, number] | null;
  yaw: number;
  pitch: number;
  /** ms spent grabbing + converting the frame. */
  grabMs: number;
  /** ms spent inside the tracker (WASM or JS). */
  trackMs: number;
  /** instantaneous fps of the tracking loop. */
  fps: number;
  /**
   * Metres per SLAM unit, once metric grounding has an estimate. Null before.
   * Recorded per sample so a replay can see when the floor was found and whether
   * the estimate stayed stable — a scale that keeps moving means the map is drifting.
   */
  metersPerUnit?: number | null;
  /**
   * How many anchors exist, and how many have committed their depth — from
   * `SpatialBackend.anchorCounts()`. Undefined on a backend that does not
   * implement it (VO, orientation); `null` is not used here, only `undefined`,
   * so a summary can tell "backend does not report this" apart from "reported
   * zero".
   *
   * Added because "barely any text anchored" (2026-08-24) was previously
   * undiagnosable after the fact: the live drawer count answers it in the
   * moment, but a captured session had no record of whether anchors were ever
   * created, or sat provisional for the whole run. This makes it visible in a
   * replay, in the same ring buffer as everything else.
   */
  anchorsTotal?: number;
  anchorsCommitted?: number;
  /**
   * The depth an anchor created on this frame WOULD be placed at, in SLAM
   * units, and which rung of the placement ladder produced it (1 = metric
   * scale, 2 = scene depth, 3 = the lottery constant).
   *
   * Added 2026-08-25 because the capture that day could not answer the only
   * question that mattered. Text was unreadable and vanished on one step, and
   * nothing recorded said how far away it had been put — `metersPerUnit` was
   * null throughout, which ruled out rung 1 but left rungs 2 and 3
   * indistinguishable. Those two produce placements an order of magnitude
   * apart (3.2 units vs ~24), with opposite symptoms, and the fix differs for
   * each. Recording the number ends the guessing.
   */
  anchorDepthUnits?: number;
  anchorDepthRung?: 1 | 2 | 3;
  /** Median scene depth in SLAM units, once triangulation has an answer. */
  sceneDepthUnits?: number | null;
  /**
   * True on the first TRACKED, posed sample after a map reset (`markReset()`): its pose
   * is in a NEW map — new origin, new arbitrary scale — so the step from the
   * previous posed sample is not motion and must not be measured as such.
   * Absent otherwise.
   */
  discontinuity?: boolean;
  /**
   * Cumulative tracker exceptions this session (AlvaAR only). Exceptions used
   * to be swallowed by a bare `catch`, so a WASM heap that had detached its
   * buffer looked exactly like "lost" for the rest of the session.
   */
  trackerErrors?: number;
}

export interface TelemetrySummary {
  backend: string;
  samples: number;
  durationMs: number;
  /** fraction of samples in 'tracking'. The headline ship gate. */
  trackingUptime: number;
  medianTrackMs: number;
  p95TrackMs: number;
  medianFps: number;
  /** Map discontinuities: explicit resets/rebuilds AND tracker-internal map restarts. */
  resets: number;
  /**
   * Of `resets`, how many the TRACKER started on its own (AlvaAR builds a new
   * map after a loss — see spatial/mapContinuity.ts). Counted separately because
   * nobody asked for these: a run with several is a run whose map kept breaking,
   * and before 2026-10-05 they were invisible (accepted as relocalisations).
   */
  mapRestarts: number;
  lostEvents: number;
  /** longest continuous stretch not tracking, ms. */
  longestGapMs: number;
  meanFeatureCount: number;
  /**
   * Straight-line distance between the first and last tracked pose.
   *
   * Only a drift measure if the walk was a closed loop — return to where you
   * started and this is the loop-closure error. On an open walk it is just how
   * far you went, so read it next to pathLength: netDisplacement ≈ pathLength
   * means a straight line, netDisplacement ≈ 0 means you came back.
   *
   * (Previously this was distance from the world origin, which is meaningless
   * whenever recording starts after tracking has already initialised — it
   * reported ~3.8 "drift" on a clean run whose real loop error was ~1.8.)
   */
  netDisplacement: number | null;
  /**
   * Total distance travelled along the tracked path. Steps that join two maps
   * across a reset (`TelemetrySample.discontinuity`) are excluded — the old
   * map's last pose and the new map's first are not a movement.
   */
  pathLength: number | null;
  /**
   * p95 step size divided by median step size — the map-integrity check.
   *
   * A camera carried by a human moves smoothly, so consecutive poses differ by
   * similar amounts and this sits near 3-5. When the map breaks, the tracker
   * teleports between competing hypotheses about where it is, and the ratio
   * explodes while the median stays normal.
   *
   * Measured on the same phone, same room, minutes apart: a healthy walk gave
   * 3.9; a rotation-heavy circuit gave **153**, with 32 jumps of ~60 units
   * carrying 89% of the reported path length. Crucially, that broken run
   * reported 100% uptime, zero resets and zero lost events — every existing gate
   * passed while the output was nonsense. This is the number that catches it.
   */
  stepRatio: number | null;
  /**
   * Implied movement speed in m/s, once metric scale is known.
   *
   * The sanity check on everything else: a person walking indoors while looking
   * at a phone does 0.5-1.2 m/s. The healthy run above implied 0.78 m/s; the
   * broken one implied 2.94 m/s, i.e. running. Independent of stepRatio, and it
   * also catches a bad scale estimate on an otherwise sound map.
   */
  impliedSpeedMps: number | null;
  /**
   * ms from telemetry start until the first sample with any anchor placed.
   * Null if the backend never reports anchor counts, or none were ever placed.
   *
   * This is the number that would have caught "barely any text anchored"
   * directly: a healthy run should show this within a few seconds of the
   * field opening. A run where it stays null despite minutes of tracking means
   * fragments existed but nothing ever called `anchor()` on them — a wiring
   * fault upstream of the spatial backend, not a tracking one.
   */
  timeToFirstAnchorMs: number | null;
  /**
   * Anchor counts at the last sample, or null if the backend never reported
   * them. `committed === 0` with `total > 0` for the whole run means anchors
   * are being created but never leaving their provisional window — i.e. every
   * anchor landed on rung 3 (the raw-unit constant) and no real depth ever
   * arrived to commit it: look at `sceneDepthUnits` and `metersPerUnit`.
   * (`ANCHOR_GRACE_MS` does NOT gate commitment; it only times mapHealth's
   * walking hint.)
   */
  finalAnchors: { total: number; committed: number } | null;
}

/** Ship gates: the numbers a build must hit before it goes in front of anyone. */
export const SHIP_GATES = {
  trackingUptime: 0.85, // ≥85% of frames tracked in a normal room
  p95TrackMs: 70, // ≤70ms p95 tracker latency
  medianFps: 12, // ≥12 Hz tracking loop
  longestGapMs: 2000, // ≤2s to recover after occlusion
  // Map integrity. AlvaAR reports healthy tracking while teleporting between
  // duplicate map regions, so this is the only gate that sees that failure.
  // 12 is comfortably above the ~4 of a good run and far below the ~153 of a
  // broken one; the two populations are orders of magnitude apart.
  stepRatio: 12,
};

export interface GateResult {
  name: keyof typeof SHIP_GATES;
  value: number;
  target: number;
  pass: boolean;
}

const MAX_SAMPLES = 3000;

function median(xs: number[]): number {
  if (xs.length === 0) return 0;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)]!;
}

function percentile(xs: number[], p: number): number {
  if (xs.length === 0) return 0;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.floor(s.length * p))]!;
}

type Listener = (s: TelemetrySample) => void;

export class SlamTelemetry {
  private samples: TelemetrySample[] = [];
  private listeners = new Set<Listener>();
  /**
   * Clock for `t` and `fps` only — diagnostics, nothing decides on them.
   * Injectable so a replay or the offline harness can make the recorded series
   * reproducible (pass the frame clock); live it is `performance.now()`.
   */
  private readonly now: () => number;
  private t0: number;
  private lastFrameTime = 0;
  private backendName = 'none';
  private resets = 0;
  private restarts = 0;
  private lostEvents = 0;
  private prevState: TrackingState = 'idle';
  /** A reset was marked and no posed sample has been recorded since. */
  private pendingDiscontinuity = false;
  /** A reset was marked since the last sample — de-dupes the state-based count. */
  private resetMarkedSinceSample = false;

  constructor(now: () => number = () => performance.now()) {
    this.now = now;
    this.t0 = now();
  }

  setBackend(name: string): void {
    this.backendName = name;
  }

  getBackend(): string {
    return this.backendName;
  }

  reset(): void {
    this.samples = [];
    this.t0 = this.now();
    this.lastFrameTime = 0;
    this.resets = 0;
    this.restarts = 0;
    this.lostEvents = 0;
    this.prevState = 'idle';
    this.pendingDiscontinuity = false;
    this.resetMarkedSinceSample = false;
  }

  /**
   * A backend has thrown its map away (reset, rebuild). Counts the reset and
   * marks the next posed sample as a discontinuity.
   *
   * ⚠️ This exists because a reset could never be counted otherwise:
   * `resetTracking()` set state 'reset', and the very next `step()` overwrote
   * the state before anything was recorded — so `resets` was 0 in every
   * capture ever taken, whatever happened. And the old map's last pose joined
   * the new map's first as one "step", in a different scale, inflating
   * pathLength, stepRatio and impliedSpeedMps.
   *
   * `kind: 'restart'` = the tracker started a new map on its own (see
   * `TelemetrySummary.mapRestarts`); it is a reset in every other respect.
   */
  markReset(kind: 'reset' | 'restart' = 'reset'): void {
    this.resets++;
    if (kind === 'restart') this.restarts++;
    this.pendingDiscontinuity = true;
    this.resetMarkedSinceSample = true;
  }

  record(s: Omit<TelemetrySample, 't' | 'fps'>): TelemetrySample {
    const now = this.now();
    const t = now - this.t0;
    const dt = this.lastFrameTime ? now - this.lastFrameTime : 0;
    this.lastFrameTime = now;
    const fps = dt > 0 ? 1000 / dt : 0;

    // A backend that records a 'reset' state without calling markReset() is
    // still counted; one that did both is counted once.
    if (s.state === 'reset' && this.prevState !== 'reset' && !this.resetMarkedSinceSample) this.resets++;
    if (s.state === 'lost' && this.prevState === 'tracking') this.lostEvents++;
    this.prevState = s.state;
    this.resetMarkedSinceSample = false;

    const sample: TelemetrySample = { ...s, t, fps };
    // Flag the first sample that will actually enter the path (tracked AND
    // posed — the same filter summary() applies), or the join slips through.
    if (this.pendingDiscontinuity && s.pos && s.state === 'tracking') {
      sample.discontinuity = true;
      this.pendingDiscontinuity = false;
    }
    this.samples.push(sample);
    if (this.samples.length > MAX_SAMPLES) this.samples.shift();

    for (const fn of this.listeners) {
      try {
        fn(sample);
      } catch {
        /* a bad listener must not break tracking */
      }
    }
    return sample;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  latest(): TelemetrySample | null {
    return this.samples[this.samples.length - 1] ?? null;
  }

  all(): TelemetrySample[] {
    return this.samples;
  }

  summary(): TelemetrySummary {
    const n = this.samples.length;
    const tracked = this.samples.filter((s) => s.state === 'tracking');
    const trackMs = this.samples.map((s) => s.trackMs);
    const fpsAll = this.samples.filter((s) => s.fps > 0).map((s) => s.fps);

    // longest continuous non-tracking gap
    let longestGap = 0;
    let gapStart: number | null = null;
    for (const s of this.samples) {
      if (s.state !== 'tracking') {
        if (gapStart === null) gapStart = s.t;
        longestGap = Math.max(longestGap, s.t - gapStart);
      } else {
        gapStart = null;
      }
    }

    // Measure against the first tracked pose, not the world origin: recording
    // usually starts well after the tracker initialised, so the origin is an
    // arbitrary point somewhere behind the participant.
    const posed = tracked.filter((s): s is typeof s & { pos: [number, number, number] } => !!s.pos);
    const first = posed[0];
    const last = posed[posed.length - 1];
    const netDisplacement =
      first && last ? Math.hypot(last.pos[0] - first.pos[0], last.pos[1] - first.pos[1], last.pos[2] - first.pos[2]) : null;
    let pathLength: number | null = null;
    const steps: number[] = [];
    if (posed.length > 1) {
      pathLength = 0;
      for (let i = 1; i < posed.length; i++) {
        // The step INTO a new map is not motion — see markReset().
        if (posed[i]!.discontinuity) continue;
        const a = posed[i - 1]!.pos;
        const b = posed[i]!.pos;
        const d = Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
        steps.push(d);
        pathLength += d;
      }
    }

    // Map integrity: smooth human motion keeps p95 close to the median. A broken
    // map teleports, which leaves the median alone and blows up the tail.
    const stepMedian = median(steps);
    const stepRatio = steps.length > 8 && stepMedian > 1e-9
      ? percentile(steps, 0.95) / stepMedian
      : null;

    // Implied speed, once we know what a unit is worth.
    const mpu = this.samples[n - 1]?.metersPerUnit ?? null;
    const durationSec = n ? this.samples[n - 1]!.t / 1000 : 0;
    const impliedSpeedMps =
      mpu != null && mpu > 0 && pathLength != null && durationSec > 1
        ? (pathLength * mpu) / durationSec
        : null;

    const firstAnchored = this.samples.find((s) => (s.anchorsTotal ?? 0) > 0);
    const timeToFirstAnchorMs = firstAnchored ? firstAnchored.t : null;
    const lastSample = this.samples[n - 1];
    const finalAnchors =
      lastSample && lastSample.anchorsTotal !== undefined
        ? { total: lastSample.anchorsTotal, committed: lastSample.anchorsCommitted ?? 0 }
        : null;

    return {
      backend: this.backendName,
      samples: n,
      durationMs: n ? this.samples[n - 1]!.t : 0,
      trackingUptime: n ? tracked.length / n : 0,
      medianTrackMs: median(trackMs),
      p95TrackMs: percentile(trackMs, 0.95),
      medianFps: median(fpsAll),
      resets: this.resets,
      mapRestarts: this.restarts,
      lostEvents: this.lostEvents,
      longestGapMs: longestGap,
      meanFeatureCount: n ? this.samples.reduce((a, s) => a + s.featureCount, 0) / n : 0,
      netDisplacement,
      pathLength,
      stepRatio,
      impliedSpeedMps,
      timeToFirstAnchorMs,
      finalAnchors,
    };
  }

  gates(): GateResult[] {
    const s = this.summary();
    return [
      { name: 'trackingUptime', value: s.trackingUptime, target: SHIP_GATES.trackingUptime, pass: s.trackingUptime >= SHIP_GATES.trackingUptime },
      { name: 'p95TrackMs', value: s.p95TrackMs, target: SHIP_GATES.p95TrackMs, pass: s.p95TrackMs <= SHIP_GATES.p95TrackMs },
      { name: 'medianFps', value: s.medianFps, target: SHIP_GATES.medianFps, pass: s.medianFps >= SHIP_GATES.medianFps },
      { name: 'longestGapMs', value: s.longestGapMs, target: SHIP_GATES.longestGapMs, pass: s.longestGapMs <= SHIP_GATES.longestGapMs },
      // Passes while there is not yet enough motion to judge — this gate exists
      // to catch a map that has broken, not to complain about a short run.
      { name: 'stepRatio', value: s.stepRatio ?? 0, target: SHIP_GATES.stepRatio, pass: s.stepRatio === null || s.stepRatio <= SHIP_GATES.stepRatio },
    ];
  }
}

/** Singleton — one tracking loop per app. */
export const telemetry = new SlamTelemetry();
